import { useEffect, useRef, useCallback, useState } from 'react';

const getBackendUrl = () => {
  const url = process.env.REACT_APP_BACKEND_URL;
  if (!url) {
    console.warn('REACT_APP_BACKEND_URL not defined, using window.location.origin');
    return window.location.origin;
  }
  return url;
};

export const useWebSocket = (onMessage) => {
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const onMessageRef = useRef(onMessage);
  const [isConnected, setIsConnected] = useState(false);

  // Update ref when callback changes
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const connect = useCallback(() => {
    // Don't connect if already connected or connecting
    if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    // Get backend URL and convert http/https to ws/wss
    const backendUrl = getBackendUrl();
    let wsUrl = backendUrl.replace(/^http/, 'ws') + '/api/ws';
    
    // Add token as query param if available
    if (token) {
      wsUrl += `?token=${encodeURIComponent(token)}`;
    }
    
    console.log('Connecting to WebSocket:', wsUrl.split('?')[0]);
    
    try {
      const socket = new WebSocket(wsUrl);
      ws.current = socket;

      socket.onopen = () => {
        console.log('WebSocket connected successfully');
        setIsConnected(true);
        
        // Send auth message with token to identify admin
        if (token && socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'auth', token }));
        }
      };

      socket.onclose = (event) => {
        console.log('WebSocket disconnected, code:', event.code);
        setIsConnected(false);
        // Only clear ws.current if it's still this socket
        if (ws.current === socket) {
          ws.current = null;
        }
        // Attempt to reconnect after 3 seconds
        if (reconnectTimeout.current) {
          clearTimeout(reconnectTimeout.current);
        }
        reconnectTimeout.current = setTimeout(connect, 3000);
      };

      socket.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data.type, data);
          if (onMessageRef.current) {
            onMessageRef.current(data);
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      reconnectTimeout.current = setTimeout(connect, 3000);
    }
  }, []);

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (ws.current) {
        ws.current.close();
        ws.current = null;
      }
    };
  }, [connect]);

  const sendMessage = useCallback((message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    }
  }, []);

  return { isConnected, sendMessage };
};

export default useWebSocket;
